"use client";

import React, { useState, use } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  Sparkles,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ShieldCheck,
  Zap,
  Code2,
  Globe,
  HelpCircle,
  Edit3,
  Send,
  MessageSquare,
  Building2,
  Workflow,
  Cpu,
  Layers,
} from "lucide-react";
import { useAdminStore } from "@/lib/store";

export default function DynamicLandingPageRoute({
  params,
}: {
  params: Promise<{ slug: string }>;
}) {
  const resolvedParams = use(params);
  const slug = resolvedParams.slug;
  const { pages, addLead } = useAdminStore();

  const page = pages.find((p) => p.slug === slug);
  const [activeFaq, setActiveFaq] = useState<number | null>(0);

  // Dynamic form state
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!page) return;

    addLead({
      name: formValues["name"] || formValues["fullName"] || "Website Visitor",
      email: formValues["email"] || formValues["businessEmail"] || "inquiry@client.com",
      phone: formValues["phone"] || formValues["phoneNumber"] || "",
      service: formValues["service"] || page.hero?.h1Title || "Custom Solution",
      budget: formValues["budget"] || "Standard",
      message: formValues["message"] || formValues["projectBrief"] || `Inquiry submitted on /${slug}`,
      status: "New",
    });

    setFormSubmitted(true);
  };

  if (!page) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-6 text-center font-['Plus_Jakarta_Sans',sans-serif]">
        <div className="w-14 h-14 rounded-2xl bg-purple-50 flex items-center justify-center text-[#711DB0] mb-4 border border-purple-200">
          <Sparkles className="w-6 h-6" />
        </div>
        <h1 className="text-xl font-bold text-slate-900">Landing Page Not Found</h1>
        <p className="text-xs text-slate-500 mt-2 max-w-md">
          The requested route <code className="text-[#711DB0] font-mono font-semibold">/{slug}</code> is not yet published in the CMS.
        </p>
        <div className="mt-6 flex items-center gap-3">
          <Link
            href="/admin/pages/editor"
            className="px-4 py-2 rounded-xl bg-[#711DB0] text-white font-medium text-xs shadow-xs hover:bg-[#5e1694] transition-colors"
          >
            Create in Admin CMS →
          </Link>
          <Link
            href="/"
            className="px-4 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 font-medium text-xs hover:bg-slate-50 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const hero = page.hero || {};
  const features = Array.isArray(page.features) ? page.features : [];
  const faqs = Array.isArray(page.faqs) ? page.faqs : [];
  const formConfig: any = page.formConfig || { enabled: true, position: "hero-inline" };
  const visible: any = page.visibleSections || {};
  const layoutSettings: any = page.layoutSettings || {};
  const sectionOrder: string[] =
    layoutSettings.sectionOrder && layoutSettings.sectionOrder.length > 0
      ? layoutSettings.sectionOrder
      : ["hero", "cards", "body", "faqs", "form"];

  const showHero = visible.hero !== false && Boolean(hero.h1Title || hero.featuredImage);
  const showCards = visible.features !== false && features.length > 0;
  const showBody = visible.bodyContent !== false && Boolean(page.bodyContent);
  const showFaqs = visible.faqs !== false && faqs.length > 0;
  const showForm = visible.form !== false && formConfig.enabled !== false;
  const formInHero = showForm && formConfig.position === "hero-inline";

  const handleCtaClick = (e: React.MouseEvent<HTMLAnchorElement>, rawLink?: string) => {
    const defaultLink = formInHero ? "#contact-form" : "#inquiry-section";
    const link = rawLink || defaultLink;
    if (link.startsWith("#")) {
      e.preventDefault();
      let targetId = link.replace(/^#/, "");
      if (targetId === "inquiry-section" || targetId === "contact-form" || targetId === "inquiry-form") {
        targetId = formInHero ? "contact-form" : "inquiry-section";
      }

      const targetEl = document.getElementById(targetId);
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: "smooth", block: "start" });
        if (targetId === "contact-form" || targetId === "inquiry-section") {
          setTimeout(() => {
            const input = targetEl.querySelector("input, textarea") as HTMLInputElement | HTMLTextAreaElement | null;
            input?.focus();
          }, 350);
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF9] text-slate-900 flex flex-col font-['Plus_Jakarta_Sans',sans-serif] selection:bg-[#711DB0] selection:text-white">
      {/* Floating Admin Toolbar */}
      <div className="fixed bottom-4 right-4 z-50">
        <Link
          href={`/admin/pages/editor?id=${page.id}`}
          className="bg-slate-900 hover:bg-[#711DB0] text-white px-4 py-2.5 rounded-full shadow-xl border border-slate-700 flex items-center gap-2 text-xs font-semibold transition-all hover:scale-105"
        >
          <Edit3 className="w-3.5 h-3.5" />
          <span>Edit Page in Studio</span>
        </Link>
      </div>

      {/* Clean Innoit Navbar */}
      <nav className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-purple-100/80 px-4 sm:px-8 lg:px-12 py-3.5 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#711DB0] flex items-center justify-center p-1.5 shadow-xs">
            <Image
              src="/images/main_logo.png"
              alt="Innoit Labs"
              width={22}
              height={22}
              className="object-contain invert brightness-200"
              priority
            />
          </div>
          <div>
            <span className="font-black text-base text-[#160A29] tracking-tight block leading-tight">
              Innoit Labs
            </span>
            <p className="text-[10px] text-purple-700 font-medium">Digital Product Engineering</p>
          </div>
        </Link>

        {/* Links */}
        <div className="hidden md:flex items-center gap-6 text-xs font-medium text-slate-600">
          <Link href="/" className="hover:text-[#711DB0] transition-colors">
            Home
          </Link>
          <Link href="/blog" className="hover:text-[#711DB0] transition-colors">
            Insights & Engineering
          </Link>
          <Link href="/admin/pages" className="text-[#711DB0] font-bold hover:underline">
            Page Management
          </Link>
        </div>

        {/* CTA */}
        <a
          href="#inquiry-section"
          className="bg-[#711DB0] hover:bg-[#5b158f] text-white font-bold px-4 py-2 rounded-xl transition-all text-xs shadow-md"
        >
          {hero.primaryCtaText || "Get Proposal"}
        </a>
      </nav>

      {/* Main Dynamic Page Content */}
      <main className="flex-1 flex flex-col">
        {sectionOrder.map((secKey) => {
          // --- 1. HERO SECTION (Signature Innoit Radial Gradient + Glows) ---
          if (secKey === "hero" && showHero) {
            return (
              <section
                key="hero"
                className="relative w-full pt-12 sm:pt-16 pb-12 sm:pb-16 px-4 sm:px-8 lg:px-12 overflow-hidden bg-[radial-gradient(ellipse_75%_55%_at_15%_100%,#F3E2FA_0%,transparent_65%),radial-gradient(ellipse_85%_60%_at_60%_100%,#FFEFE6_0%,transparent_70%),linear-gradient(180deg,#FFFFFF_0%,#FFFFFF_38%,#FFF8F3_70%,#FFEFE6_100%)] border-b border-purple-100/60"
              >
                {/* Ambient glow spheres */}
                <div className="absolute -bottom-10 left-0 w-[550px] h-[300px] bg-gradient-to-tr from-[#F1DCF8]/70 via-[#FDF0F6]/40 to-transparent rounded-full blur-2xl pointer-events-none" />
                <div className="absolute -bottom-12 left-[30%] w-[600px] h-[280px] bg-gradient-to-t from-[#FFEADB]/80 via-[#FFF5ED]/40 to-transparent rounded-full blur-2xl pointer-events-none" />

                <div className="relative z-10 max-w-7xl mx-auto">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    {/* Left Column (7 cols) */}
                    <div className="lg:col-span-7 space-y-5 text-left">
                      {hero.badge && (
                        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#FAF0FF] text-[#711DB0] text-xs font-bold border border-purple-200">
                          <Code2 className="w-3.5 h-3.5 text-[#711DB0]" />
                          <span>{hero.badge}</span>
                        </div>
                      )}

                      <h1 className="text-3xl sm:text-4xl lg:text-[46px] font-black tracking-tight leading-[1.12] text-[#160A29]">
                        {hero.h1Title || "Custom Software Development"}{" "}
                        {hero.titleHighlight && (
                          <span className="bg-gradient-to-r from-[#711DB0] via-purple-600 to-indigo-600 bg-clip-text text-transparent">
                            {hero.titleHighlight}
                          </span>
                        )}
                      </h1>

                      {hero.subheading && (
                        <p className="text-sm sm:text-base text-slate-600 leading-relaxed font-normal max-w-2xl">
                          {hero.subheading}
                        </p>
                      )}

                      {/* 3 Key Metrics Row */}
                      <div className="grid grid-cols-3 gap-4 pt-3 pb-3 border-y border-purple-100/80 max-w-lg">
                        <div>
                          <p className="text-xl sm:text-2xl font-black text-[#711DB0]">16+ Yrs</p>
                          <p className="text-xs text-slate-500 font-medium">Bespoke Experience</p>
                        </div>
                        <div>
                          <p className="text-xl sm:text-2xl font-black text-[#711DB0]">6+ Regions</p>
                          <p className="text-xs text-slate-500 font-medium">Global Delivery</p>
                        </div>
                        <div>
                          <p className="text-xl sm:text-2xl font-black text-[#711DB0]">100%</p>
                          <p className="text-xs text-slate-500 font-medium">IP Code Ownership</p>
                        </div>
                      </div>

                      {/* CTAs */}
                      <div className="flex flex-wrap items-center gap-3.5 pt-1">
                        {hero.primaryCtaText && (() => {
                          const link = hero.primaryCtaLink || (formInHero ? "#contact-form" : "#inquiry-section");
                          const isAnchor = link.startsWith("#");
                          const isExternal = link.startsWith("http");
                          const isTelOrMail = link.startsWith("tel:") || link.startsWith("mailto:");

                          if (isAnchor) {
                            return (
                              <a
                                href={link}
                                onClick={(e) => handleCtaClick(e, link)}
                                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#711DB0] hover:bg-[#5b158f] text-white font-bold text-xs sm:text-sm shadow-md transition-all cursor-pointer"
                              >
                                <span>{hero.primaryCtaText}</span>
                                <ArrowRight className="w-4 h-4" />
                              </a>
                            );
                          }

                          if (isExternal) {
                            return (
                              <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#711DB0] hover:bg-[#5b158f] text-white font-bold text-xs sm:text-sm shadow-md transition-all cursor-pointer"
                              >
                                <span>{hero.primaryCtaText}</span>
                                <ArrowRight className="w-4 h-4" />
                              </a>
                            );
                          }

                          if (isTelOrMail) {
                            return (
                              <a
                                href={link}
                                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#711DB0] hover:bg-[#5b158f] text-white font-bold text-xs sm:text-sm shadow-md transition-all cursor-pointer"
                              >
                                <span>{hero.primaryCtaText}</span>
                                <ArrowRight className="w-4 h-4" />
                              </a>
                            );
                          }

                          return (
                            <Link
                              href={link}
                              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#711DB0] hover:bg-[#5b158f] text-white font-bold text-xs sm:text-sm shadow-md transition-all cursor-pointer"
                            >
                              <span>{hero.primaryCtaText}</span>
                              <ArrowRight className="w-4 h-4" />
                            </Link>
                          );
                        })()}

                        {hero.secondaryCtaText && (() => {
                          const link = hero.secondaryCtaLink || "#capabilities-section";
                          const isAnchor = link.startsWith("#");
                          const isExternal = link.startsWith("http");
                          const isTelOrMail = link.startsWith("tel:") || link.startsWith("mailto:");

                          if (isAnchor) {
                            return (
                              <a
                                href={link}
                                onClick={(e) => handleCtaClick(e, link)}
                                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white text-slate-800 font-semibold text-xs sm:text-sm border border-slate-200 shadow-2xs hover:bg-slate-50 transition-colors cursor-pointer"
                              >
                                <span>{hero.secondaryCtaText}</span>
                              </a>
                            );
                          }

                          if (isExternal) {
                            return (
                              <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white text-slate-800 font-semibold text-xs sm:text-sm border border-slate-200 shadow-2xs hover:bg-slate-50 transition-colors cursor-pointer"
                              >
                                <span>{hero.secondaryCtaText}</span>
                              </a>
                            );
                          }

                          if (isTelOrMail) {
                            return (
                              <a
                                href={link}
                                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white text-slate-800 font-semibold text-xs sm:text-sm border border-slate-200 shadow-2xs hover:bg-slate-50 transition-colors cursor-pointer"
                              >
                                <span>{hero.secondaryCtaText}</span>
                              </a>
                            );
                          }

                          return (
                            <Link
                              href={link}
                              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white text-slate-800 font-semibold text-xs sm:text-sm border border-slate-200 shadow-2xs hover:bg-slate-50 transition-colors cursor-pointer"
                            >
                              <span>{hero.secondaryCtaText}</span>
                            </Link>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Right Column (5 cols): Interactive Form or Graphic */}
                    <div className="lg:col-span-5 flex justify-center lg:justify-end w-full">
                      {formInHero ? (
                        <div id="contact-form" className="w-full max-w-[460px] bg-white rounded-3xl p-6 sm:p-7 border border-purple-200 shadow-xl space-y-4 text-left">
                          <div className="space-y-1 text-center">
                            <h3 className="text-base font-bold text-[#160A29]">
                              {formConfig.title || "Request Technical Architecture Proposal"}
                            </h3>
                            <p className="text-xs text-slate-500">
                              {formConfig.subtitle || "Share your requirements. We respond within 2 hours."}
                            </p>
                          </div>

                          {formSubmitted ? (
                            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-center space-y-1">
                              <CheckCircle2 className="w-6 h-6 text-emerald-600 mx-auto" />
                              <p className="text-sm font-bold text-emerald-900">Brief Submitted!</p>
                              <p className="text-xs text-emerald-700">
                                {formConfig.successMessage || "We will contact you shortly."}
                              </p>
                            </div>
                          ) : (
                            <form onSubmit={handleFormSubmit} className="space-y-3">
                              {(formConfig.fields || [
                                { id: "1", name: "fullName", label: "Full Name", type: "text", placeholder: "Your Name", required: true },
                                { id: "2", name: "businessEmail", label: "Work Email", type: "email", placeholder: "you@company.com", required: true },
                                { id: "3", name: "phoneNumber", label: "Phone", type: "tel", placeholder: "Mobile Number", required: false },
                              ]).slice(0, 4).map((fld: any) => (
                                <div key={fld.id}>
                                  <input
                                    type={fld.type}
                                    placeholder={fld.placeholder || fld.label}
                                    value={formValues[fld.name] || ""}
                                    onChange={(e) =>
                                      setFormValues({ ...formValues, [fld.name]: e.target.value })
                                    }
                                    required={fld.required}
                                    className="w-full bg-[#FAF7FD] border border-purple-100 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:bg-white focus:border-[#711DB0] outline-none"
                                  />
                                </div>
                              ))}
                              <button
                                type="submit"
                                className="w-full py-3 bg-[#711DB0] hover:bg-[#5e1694] text-white font-bold text-xs sm:text-sm rounded-xl shadow-md transition-all cursor-pointer"
                              >
                                {formConfig.submitButtonText || "Submit Project Brief"}
                              </button>
                            </form>
                          )}
                        </div>
                      ) : (
                        <div className="w-full max-w-[460px]">
                          <div className="rounded-3xl overflow-hidden shadow-[0_12px_36px_rgba(113,29,176,0.1)] border border-purple-100 bg-[#1C0A2E]">
                            {hero.featuredImage ? (
                              <img
                                src={hero.featuredImage}
                                alt={hero.featuredImageAlt || hero.h1Title || "Hero"}
                                className="w-full h-auto object-cover transform hover:scale-102 transition-transform duration-500"
                              />
                            ) : (
                              <div className="w-full aspect-[4/3] bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center p-8 text-center">
                                <Globe className="w-16 h-16 text-purple-400/80 animate-pulse" />
                              </div>
                            )}
                          </div>
                          {hero.featuredImageCaption && (
                            <p className="text-[11px] text-center text-slate-500 mt-2 italic font-medium">
                              {hero.featuredImageCaption}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            );
          }

          if (secKey === "cards" && showCards) {
            return (
              <section key="cards" id="capabilities-section" className="py-14 sm:py-16 px-4 sm:px-8 lg:px-12 bg-white border-b border-purple-100/60">
                <div className="max-w-6xl mx-auto space-y-8 text-center">
                  <div className="space-y-2">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FAF0FF] text-[#711DB0] text-xs font-bold uppercase tracking-wider border border-purple-100">
                      <Sparkles className="w-3.5 h-3.5 text-[#711DB0]" />
                      <span>Key Architectural Capabilities</span>
                    </div>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#160A29] tracking-tight">
                      Engineered for Performance &amp; Scalability
                    </h2>
                    <div className="w-16 h-1 bg-[#711DB0] mx-auto rounded-full" />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 text-left">
                    {features.map((c: any, idx: number) => (
                      <div
                        key={c.id || idx}
                        className="group relative flex flex-col justify-between p-6 sm:p-7 rounded-2xl bg-[#FAF7FD] border border-purple-100 hover:border-purple-300 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 shadow-2xs overflow-hidden h-full"
                      >
                        {/* Left gradient accent line that highlights on hover */}
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#C084FC] via-[#A855F7] to-[#711DB0] opacity-50 group-hover:opacity-100 group-hover:w-1.5 transition-all duration-300" />

                        <div className="space-y-3.5">
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-xs bg-purple-100/80 text-purple-800 border border-purple-200 px-2 py-0.5 rounded font-bold">
                              {c.badge || `0${idx + 1}`}
                            </span>
                            <div className="w-9 h-9 rounded-xl bg-purple-100 border border-purple-200 flex items-center justify-center text-[#711DB0]">
                              <Sparkles className="w-4 h-4" />
                            </div>
                          </div>
                          <p className="font-bold text-[#160A29] text-base group-hover:text-[#711DB0] transition-colors">
                            {c.title}
                          </p>
                          <p className="text-slate-600 text-xs sm:text-sm leading-relaxed font-normal">
                            {c.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            );
          }

          // --- 3. RICH BODY CONTENT ---
          if (secKey === "body" && showBody) {
            return (
              <section key="body" id="deep-dive-section" className="py-14 sm:py-16 px-4 sm:px-8 lg:px-12 bg-[#FAF7FD]">
                <div className="max-w-4xl mx-auto">
                  <article className="bg-white rounded-3xl border border-purple-100 p-8 sm:p-12 shadow-xs">
                    <div
                      className="space-y-5 text-slate-700 leading-relaxed text-sm sm:text-base [&_h2]:text-2xl sm:[&_h2]:text-3xl [&_h2]:font-extrabold [&_h2]:text-[#160A29] [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:text-lg sm:[&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-[#160A29] [&_h3]:mt-6 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1.5 [&_p]:mb-4 [&_a]:text-[#711DB0] [&_a]:font-semibold [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-[#711DB0] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:bg-purple-50/50 [&_blockquote]:py-2 [&_blockquote]:my-4 [&_table]:w-full [&_table]:border-collapse [&_table]:my-6 [&_table]:rounded-xl [&_table]:overflow-hidden [&_table]:border [&_table]:border-purple-200 [&_th]:bg-purple-900/10 [&_th]:font-bold [&_th]:text-[#160A29] [&_th]:p-3 [&_th]:text-left [&_th]:border [&_th]:border-purple-200 [&_td]:p-3 [&_td]:border [&_td]:border-purple-100 [&_td]:text-slate-700 [&_img]:rounded-2xl [&_img]:my-6"
                      dangerouslySetInnerHTML={{ __html: page.bodyContent || "" }}
                    />
                  </article>
                </div>
              </section>
            );
          }

          // --- 4. FAQS ACCORDION SECTION ---
          if (secKey === "faqs" && showFaqs) {
            return (
              <section key="faqs" id="faqs-section" className="py-14 sm:py-16 px-4 sm:px-8 lg:px-12 bg-white border-t border-purple-100/60">
                <div className="max-w-4xl mx-auto space-y-8 text-center">
                  <div className="space-y-2">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FAF0FF] text-[#711DB0] text-xs font-bold uppercase tracking-wider border border-purple-100">
                      <HelpCircle className="w-3.5 h-3.5 text-[#711DB0]" />
                      <span>FAQS</span>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-extrabold text-[#160A29] tracking-tight">
                      Frequently Asked Questions
                    </h2>
                    <div className="w-16 h-1 bg-[#711DB0] mx-auto rounded-full" />
                  </div>

                  <div className="space-y-3 text-left">
                    {faqs.map((faq: any, i: number) => {
                      const isOpen = activeFaq === i;
                      return (
                        <div
                          key={faq.id || i}
                          className="bg-[#FAF8FE] border border-purple-100 rounded-2xl overflow-hidden transition-all duration-200"
                        >
                          <button
                            type="button"
                            onClick={() => setActiveFaq(isOpen ? null : i)}
                            className={`w-full flex items-center justify-between px-5 sm:px-6 py-4 text-left transition-colors cursor-pointer ${isOpen ? "bg-purple-50/80" : "hover:bg-purple-50/40"
                              }`}
                          >
                            <span className="text-sm sm:text-base font-bold text-[#160A29] pr-4">
                              {faq.question}
                            </span>
                            <div
                              className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-transform duration-200 ${isOpen ? "bg-[#711DB0] text-white rotate-180" : "bg-purple-100 text-[#711DB0]"
                                }`}
                            >
                              <ChevronDown className="w-3.5 h-3.5" />
                            </div>
                          </button>
                          {isOpen && (
                            <div className="px-5 sm:px-6 py-4 bg-white border-t border-purple-100 text-xs sm:text-sm text-slate-600 leading-relaxed">
                              {faq.answer}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            );
          }

          // --- 5. DEDICATED INQUIRY FORM (If position !== "hero-inline") ---
          if (secKey === "form" && showForm && !formInHero) {
            return (
              <section
                key="form"
                id="inquiry-section"
                className="py-14 sm:py-20 px-4 sm:px-8 lg:px-12 bg-gradient-to-b from-white via-purple-50/20 to-[#FAF7FD] border-t border-purple-100/80"
              >
                <div className="max-w-2xl mx-auto space-y-6">
                  <div className="text-center space-y-1.5">
                    <h3 className="text-2xl sm:text-3xl font-extrabold text-[#160A29]">
                      {formConfig.title || "Request Technical Architecture Proposal"}
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-600">
                      {formConfig.subtitle || "Share your requirements. We will respond within 2 hours."}
                    </p>
                  </div>

                  {formSubmitted ? (
                    <div className="p-6 rounded-2xl bg-emerald-50 border border-emerald-200 text-center space-y-2 animate-scale-pop">
                      <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto" />
                      <p className="text-base font-bold text-emerald-900">
                        Inquiry Received Successfully!
                      </p>
                      <p className="text-xs text-emerald-700">
                        {formConfig.successMessage || "Thank you! We will contact you shortly."}
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleFormSubmit} className="space-y-4 bg-white p-7 rounded-3xl border border-purple-100 shadow-md">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {(formConfig.fields || [
                          { id: "1", name: "fullName", label: "Full Name", type: "text", placeholder: "Your Name", required: true },
                          { id: "2", name: "businessEmail", label: "Work Email", type: "email", placeholder: "you@company.com", required: true },
                          { id: "3", name: "phoneNumber", label: "Phone", type: "tel", placeholder: "Mobile Number", required: false },
                          { id: "4", name: "projectBrief", label: "Project Brief", type: "textarea", placeholder: "Brief requirements...", required: false },
                        ]).map((fld: any) => (
                          <div
                            key={fld.id}
                            className={fld.type === "textarea" ? "sm:col-span-2" : "col-span-1"}
                          >
                            <label className="block text-xs font-semibold text-slate-700 mb-1">
                              {fld.label} {fld.required && <span className="text-rose-500">*</span>}
                            </label>
                            <input
                              type={fld.type === "textarea" ? "text" : fld.type}
                              placeholder={fld.placeholder || fld.label}
                              value={formValues[fld.name] || ""}
                              onChange={(e) =>
                                setFormValues({ ...formValues, [fld.name]: e.target.value })
                              }
                              required={fld.required}
                              className="w-full bg-[#FAF7FD] border border-purple-100 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 focus:bg-white focus:border-[#711DB0] outline-none"
                            />
                          </div>
                        ))}
                      </div>

                      <button
                        type="submit"
                        className="w-full bg-[#711DB0] hover:bg-[#5e1694] text-white font-bold py-3 rounded-xl text-xs sm:text-sm shadow-md transition-all cursor-pointer"
                      >
                        {formConfig.submitButtonText || "Submit Inquiry"}
                      </button>
                    </form>
                  )}
                </div>
              </section>
            );
          }

          return null;
        })}

        {/* ARCHITECTURAL CONSOLE (Summary & Next Steps) */}
        <section id="summary-section" className="py-14 sm:py-16 px-4 sm:px-8 lg:px-12 bg-[#FAF7FD] border-t border-purple-100/60">
          <div className="max-w-5xl mx-auto rounded-3xl bg-white border border-purple-100 shadow-sm p-8 sm:p-10 space-y-5 text-left">
            <div className="space-y-2">
              <span className="text-xs font-bold text-[#711DB0] bg-[#FAF0FF] px-3 py-1 rounded-full border border-purple-100">
                Summary &amp; Next Steps
              </span>
              <h3 className="text-xl sm:text-2xl font-black text-[#160A29]">
                {hero.h1Title || "Custom Solution Architecture"}
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                {hero.subheading ||
                  "Engineered to match your business workflows precisely. Contact our team to begin your technical discovery."}
              </p>
            </div>
            <div className="border-t border-purple-100 pt-5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm font-bold text-[#711DB0]">Ready to Build Custom Solutions?</p>
              <a
                href="#inquiry-section"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#711DB0] text-white text-xs sm:text-sm font-bold shadow-md hover:bg-[#5e1694] transition-all"
              >
                <span>Start a Conversation</span>
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="w-full bg-slate-900 text-slate-400 py-10 px-4 sm:px-8 lg:px-12 text-center text-xs border-t border-slate-800 space-y-3 mt-auto">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <span className="font-bold text-white text-sm">Innoit Labs</span>
            <span>•</span>
            <span className="text-slate-500">HITEC City, Hyderabad • Global Delivery</span>
          </div>

          <div className="flex items-center gap-4 text-slate-400">
            <Link href="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <Link href="/blog" className="hover:text-white transition-colors">
              Engineering Blogs
            </Link>
            <Link href="/admin/pages" className="text-purple-400 font-semibold hover:underline">
              CMS Pages
            </Link>
          </div>

          <p className="text-slate-500">© {new Date().getFullYear()} Innoit Labs. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
